<script context="module" lang="ts">
  // Both filter panels are in the DOM at once -- the desktop sidebar and the mobile sheet each
  // render one -- so a row's id has to say which list it belongs to, or the sidebar's `for` would
  // point at the sheet's checkbox for the same athlete.
  let nextListId = 0;
</script>

<script lang="ts">
  // Narrowing a coach or squad dashboard to particular athletes. Only those two pages mount it --
  // the personal dashboard has no roster to choose from -- and the roster arrives as a prop from
  // the page, which already knows exactly whose activities its endpoint will return.
  //
  // A checkbox list rather than a dropdown: it has to work identically in the desktop sidebar and
  // in the mobile filter sheet, and picking several athletes in a row shouldn't mean reopening a
  // menu between each one. Nothing selected means everyone, which is also what the page shows
  // before the filter is touched, so there is no "All" row to keep in sync with the rest.
  import { Button } from '@/lib/components/ui/button';
  import { Checkbox } from '@/lib/components/ui/checkbox';
  import { Input } from '@/lib/components/ui/input';
  import Avatar from '@/Avatar.svelte';
  import { cn } from '@/lib/utils';
  import { ownerName, type ActivityFilterFields, type ActivityOwnerOption } from './ActivityFilterFields';

  export let fields: ActivityFilterFields;
  export let owners: ActivityOwnerOption[] = [];
  /** "Athletes" on a coach page, "Members" on a squad one. */
  export let title: string = 'Athletes';

  // Long enough that scrolling the list is worse than typing a name. A coach's roster is usually
  // well under this; a big squad is not.
  const SEARCH_FROM = 8;

  const listId = nextListId++;
  const rowId = (ownerId: number) => `member-${listId}-${ownerId}`;

  let search = '';
  $: sorted = [...owners].sort((a, b) => ownerName(a).localeCompare(ownerName(b)));
  $: needle = search.trim().toLowerCase();
  $: shown = needle
    ? sorted.filter((owner) => `${ownerName(owner)} ${owner.username}`.toLowerCase().includes(needle))
    : sorted;
  $: selectedCount = fields.members.length;
</script>

{#if owners.length}
  <div>
    <div class="flex flex-row items-center justify-between gap-2">
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">
        {title}{#if selectedCount}<span class="ml-1 text-xs">({selectedCount})</span>{/if}
      </h3>
      {#if selectedCount}
        <Button
          variant="ghost"
          size="sm"
          class="text-xs h-7 px-2"
          on:click={() => { fields.clearMembers(); fields = fields; }}
        >
          Clear
        </Button>
      {/if}
    </div>

    {#if owners.length >= SEARCH_FROM}
      <Input class="mb-1 h-8" placeholder="Find someone..." bind:value={search} />
    {/if}

    <div
      class={cn(
        'flex flex-col rounded-md border border-solid overflow-y-auto max-h-48',
        selectedCount ? 'border-2 border-blue-400' : 'border-border'
      )}
    >
      {#each shown as owner (owner.id)}
        {@const checked = fields.members.includes(owner.id)}
        <!-- The label is the whole row, so the name and the avatar are part of the hit target
             rather than dead space beside a 16px box. -->
        <label
          for={rowId(owner.id)}
          class={cn(
            'flex flex-row items-center gap-2 px-2 py-1.5 m-0 cursor-pointer font-normal hover:bg-accent',
            checked ? 'bg-accent/60' : ''
          )}
        >
          <!-- border-border, because the default border-primary is near-black and turns a list of
               athletes into a column of heavy boxes. -->
          <Checkbox
            id={rowId(owner.id)}
            class="border-border"
            {checked}
            onCheckedChange={() => { fields.toggleMember(owner.id); fields = fields; }}
          />
          <Avatar
            shape="circle"
            pad_right={false}
            username={owner.username}
            first_name={owner.first_name ?? ''}
            last_name={owner.last_name ?? ''}
            profile_photo={owner.profile_photo ?? ''}
            --avatar-height="20px"
          />
          <span class="text-sm truncate">{ownerName(owner)}</span>
        </label>
      {:else}
        <p class="text-xs text-muted-foreground m-0 px-2 py-2">No one by that name.</p>
      {/each}
    </div>
  </div>
{/if}
